/**
 * Making an automated step VISIBLE.
 *
 * A flow step that presses buttons on the user's behalf has a problem the
 * highlight ring alone cannot solve: the ring sits on the control that started
 * the action (Add block), while everything the action DOES happens somewhere
 * else — a menu opens, an item is chosen, a new row appears further down a
 * scrolling panel. Run at machine speed that reads as "I pressed Next and the
 * form changed by itself", which is exactly the report this module exists to
 * answer: the guide said it was adding a field and the user never saw a field
 * being added.
 *
 * So each stage of an automated sequence is announced the way a person would
 * do it slowly: hold on the thing about to be pressed, pulse it, then press.
 * The pulse is a pure CSS class (`tour-task-flash`, in `tour-guide.css`) with a
 * self-clearing animation — it changes nothing about layout or hit-testing, so
 * a flash left behind by an interrupted flow is inert.
 */

import { resolveAnchor, type AnchorSpec } from '../engine/anchors';
import { delay } from './formFill';

/** The pulse class, and the row-sized variant that holds longer. */
const FLASH_CLASS = 'tour-task-flash';
const FLASH_ROW_CLASS = 'tour-task-flash--row';

/**
 * Pulse an element and resolve once the pulse has been on screen long enough
 * to register.
 *
 * `holdMs` is deliberately generous (a third of a second is below the
 * threshold at which a change reads as a change). The class is removed
 * afterwards so the same element can be pulsed again later in the flow —
 * re-adding a class that is already there restarts nothing.
 */
export async function flashElement(
  el: HTMLElement | null,
  { holdMs = 520, row = false }: { holdMs?: number; row?: boolean } = {},
): Promise<void> {
  if (!el) return;
  const className = row ? FLASH_ROW_CLASS : FLASH_CLASS;
  el.classList.add(className);
  await delay(holdMs);
  el.classList.remove(className);
}

/** `flashElement` for something described by an anchor. No-ops when it does not resolve. */
export function flashAnchor(spec: AnchorSpec, options?: { holdMs?: number; row?: boolean }): Promise<void> {
  return flashElement(resolveAnchor(spec)?.element ?? null, options);
}

/**
 * Bring an element into view the way the task engine does — instantly.
 *
 * 🔴 NOT `behavior: 'smooth'`, for the same reason `TaskEngine.resolve` gives:
 * the ring is re-measured on a timer, so anything that animates its target for
 * a few hundred milliseconds draws the ring against a position the element has
 * already left.
 */
export function bringIntoView(el: HTMLElement | null): void {
  if (!el) return;
  try {
    el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
  } catch {
    el.scrollIntoView();
  }
}
